/**
 * @module stores/assistantStorage
 * @description 어시스턴트 대화 새로고침 복원(AA-143) — sessionStorage 저장·로드·삭제와 대화 작업(task) 어휘.
 * Pinia 에 의존하지 않는다: api/interceptors.js(세션 만료)가 스토어 활성화 없이 clearConversation 을 부른다.
 * 저장 주인은 sessionStorage authToken 의 JWT sub(loginId) — 탭 단위이고 X-CS-Token 과 같은 값이다.
 */
import jwtDecode from 'jwt-decode'
import { isValidBlock } from '@/composables/blockGuard'

export const STORAGE_KEY = 'assistant-conversation'
/** 저장 메시지 상한 — 서버 LLM 입력(10)·구 결정표 입력(40)을 덮는다 */
export const MAX_PERSIST_MESSAGES = 50
/** cs-ai-agent app/core/draft_store.py _TTL_SECONDS(300) 와 동기. 최종 만료 판정은 commit 응답이다 */
export const DRAFT_TTL_MS = 300000

/** 초안 카드 블록 타입 → 대화 작업(task). 서버 state.task 어휘(repair|consultation|admin)와 같다 */
export const DRAFT_TASK = { draft_confirm: 'repair', consultation_draft: 'consultation', admin_draft: 'admin' }
const TASKS = Object.values(DRAFT_TASK)
const KINDS = ['user', 'bot', 'error', 'block']
const RESTORE_NOTES = ['expired', 'commitUnknown']
// 복원에 필요한 필드만 저장한다(_uid 는 pushMessage 가 다시 매기고, _started·statusChip 은 스트리밍 중 임시값)
const PERSIST_FIELDS = ['kind', 'text', 'block', 'committing', 'done', 'recvAt', 'restoreNote']
// 끝난(done) 초안 카드에서 빼고 저장하는 commit 키 — 닫힌 카드는 다시 등록하지 않는다(보관 범위 축소)
const COMMIT_KEYS = ['draft_id', 'confirmation_token']

/**
 * 블록이 초안 카드면 그 작업(task), 아니면 null.
 * @param {object} block - { type, data }
 * @returns {string|null}
 */
export function taskOfBlock(block) {
  return (block && DRAFT_TASK[block.type]) || null
}

/**
 * 알 수 없는 task 값을 null 로 정규화한다(손상된 저장분·서버 신규 값이 요청을 깨지 않게).
 * @param {*} v
 * @returns {string|null}
 */
export function sanitizeTask(v) {
  return TASKS.includes(v) ? v : null
}

/**
 * 현재 탭 로그인 사용자 식별자(JWT sub). 토큰이 없거나 JWT 가 아니면 ''.
 * @returns {string}
 */
export function currentOwner() {
  try {
    const token = sessionStorage.getItem('authToken')
    const sub = token ? jwtDecode(token).sub : ''
    return typeof sub === 'string' ? sub : ''
  } catch (e) {
    return ''
  }
}

/** 저장된 대화를 지운다 — 로그아웃·로그인(reset)·세션 만료 경로 */
export function clearConversation() {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch (e) { /* 저장소 접근 불가 — 무시 */ }
}

/**
 * 대화 스냅샷을 저장한다. 주인(JWT sub)을 알 수 없으면 저장하지 않는다.
 * @param {{greeted:boolean, briefed:boolean, task:string|null, messages:object[]}} state
 */
export function saveConversation(state) {
  const owner = currentOwner()
  if (!owner) return
  const messages = state.messages.slice(-MAX_PERSIST_MESSAGES).map((m) => {
    const out = {}
    for (const k of PERSIST_FIELDS) if (m[k] !== undefined) out[k] = m[k]
    if (m.done && taskOfBlock(m.block) && m.block.data) {
      const data = { ...m.block.data } // 사본 — 화면의 카드 데이터는 건드리지 않는다
      for (const k of COMMIT_KEYS) delete data[k]
      out.block = { ...m.block, data }
    }
    return out
  })
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      v: 1, owner, greeted: !!state.greeted, briefed: !!state.briefed, task: sanitizeTask(state.task), messages
    }))
  } catch (e) {
    // 용량 초과 등 — 오래된 스냅샷이 복원되느니 비운다
    clearConversation()
  }
}

/**
 * 저장 블록의 구조 검증(Output Guard 규칙 그대로). done 초안 카드는 commit 키 없이 저장되므로
 * (admin_draft 는 두 키가 필수) 그 키를 채운 사본으로 검증한다.
 * @param {object} m - 저장 메시지(kind 'block')
 * @returns {boolean}
 */
function isRestorable(m) {
  const d = m.block && m.block.data
  if (!(m.done && taskOfBlock(m.block) && d && typeof d === 'object')) return isValidBlock(m.block)
  const filled = { ...d }
  for (const k of COMMIT_KEYS) filled[k] = '-'
  return isValidBlock({ ...m.block, data: filled })
}

/**
 * 저장 메시지를 복원용으로 정리한다. 형식이 깨진 메시지는 그 메시지만 버린다.
 * - 빈 bot = 응답 중 새로고침된 placeholder → noteKey 안내(error — 대화 이력에서 빠진다)
 * - 등록 중이던 초안 카드 → 결과를 모르므로 닫고 commitUnknown(중복 등록 방지)
 * - 미완료 초안 카드가 TTL 을 넘겼거나 수신 시각이 없으면 expired(카드는 누를 수 있게 둔다)
 * @param {object[]} list
 * @param {number} now - 기준 시각(ms)
 * @returns {object[]}
 */
function restoreMessages(list, now) {
  const out = []
  for (const m of list) {
    if (!m || !KINDS.includes(m.kind)) continue
    if (m.kind !== 'block') {
      if (typeof m.text !== 'string') continue
      if (m.kind === 'bot' && m.text === '') out.push({ kind: 'error', noteKey: 'assistant.interruptedByRefresh' })
      else out.push({ kind: m.kind, text: m.text })
      continue
    }
    if (!isRestorable(m)) continue
    if (!taskOfBlock(m.block)) {
      out.push({ kind: 'block', block: m.block })
      continue
    }
    const card = { kind: 'block', block: m.block, committing: false, done: !!m.done }
    if (typeof m.recvAt === 'number') card.recvAt = m.recvAt
    if (RESTORE_NOTES.includes(m.restoreNote)) card.restoreNote = m.restoreNote
    if (m.committing === true) {
      card.done = true
      card.restoreNote = 'commitUnknown'
    } else if (!card.done && !card.restoreNote && (card.recvAt === undefined || now - card.recvAt > DRAFT_TTL_MS)) {
      card.restoreNote = 'expired'
    }
    out.push(card)
  }
  return out
}

/**
 * 현재 사용자의 저장 대화를 읽는다. 없으면 null. 손상·버전 불일치·주인 불일치면 키를 지우고 null.
 * @param {number} [now] - 기준 시각(ms)
 * @returns {{greeted:boolean, briefed:boolean, task:string|null, messages:object[]}|null}
 */
export function loadConversation(now = Date.now()) {
  let snap
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw == null) return null
    snap = JSON.parse(raw)
  } catch (e) {
    snap = null
  }
  const owner = currentOwner()
  if (!snap || snap.v !== 1 || !owner || snap.owner !== owner || !Array.isArray(snap.messages)) {
    clearConversation()
    return null
  }
  return {
    greeted: !!snap.greeted,
    briefed: !!snap.briefed,
    task: sanitizeTask(snap.task),
    messages: restoreMessages(snap.messages, now)
  }
}
